import React, { forwardRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useGetCopilotModelUsersQuery, useGetEnterpriseCopilotModelUsersQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useDebounce } from "utils/useDebounce";

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const HEADER_BG = "#0f2044";
const ACCENT = "#0097D7";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";

const AVATAR_COLORS = ["#1F7AA8", "#0097D7", "#7B5EA7", "#E87722", "#43B02A", "#E2231A", "#F28C00"];

const getInitials = (username) => {
  if (!username) return "?";
  const clean = username.replace(/_deloitte$/, "").replace(/_/g, " ");
  const parts = clean.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : clean.slice(0, 2).toUpperCase();
};

const getAvatarColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const fmt = (v) => (v ?? 0).toLocaleString("en-AU", { maximumFractionDigits: 2 });
const fmtUSD = (v) =>
  "$" + (v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const MiniStat = ({ label, value, color }) => (
  <Box
    sx={{
      flex: 1,
      backgroundColor: "rgba(255,255,255,0.07)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "10px",
      px: 2,
      py: 1.2,
    }}
  >
    <Typography
      sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.06em" }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "1.05rem", fontWeight: 700, color: color ?? "#fff", lineHeight: 1.3, mt: 0.2 }}
    >
      {value}
    </Typography>
  </Box>
);

const hCell = {
  fontWeight: 700,
  fontSize: "0.7rem",
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#f7f7f7",
  borderBottom: "2px solid #e8e8e8",
  py: 1.2,
  whiteSpace: "nowrap",
};

const bCell = {
  fontSize: "0.82rem",
  color: "#1a1a1a",
  py: 1,
  borderBottom: "1px solid #f0f0f0",
};

const ModelDetailModal = ({ modelName, modelStats, open, onClose, orgId, costCenterId, platformId, fromDate, toDate, isEnterprise = false }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("gross_quantity");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const perPage = 20;

  const debouncedSearch = useDebounce(searchQuery, 300);

  // Reset controls when a different model is opened
  React.useEffect(() => {
    if (modelName) {
      setSearchQuery("");
      setSortBy("gross_quantity");
      setSortOrder("desc");
      setPage(1);
    }
  }, [modelName]);

  const skip = isEnterprise
    ? !open || !modelName || !platformId
    : !open || !modelName || !(orgId || costCenterId) || !platformId;

  const scopedQuery = useGetCopilotModelUsersQuery(
    { modelName, orgId, costCenterId, platformId, fromDate, toDate, search: debouncedSearch, sortBy, sortOrder, page, perPage },
    { skip: skip || isEnterprise }
  );
  const enterpriseQuery = useGetEnterpriseCopilotModelUsersQuery(
    { modelName, platformId, fromDate, toDate, search: debouncedSearch, sortBy, sortOrder, page, perPage },
    { skip: skip || !isEnterprise }
  );
  const { data, isLoading, isFetching } = isEnterprise ? enterpriseQuery : scopedQuery;

  const users = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const loading = isLoading || isFetching;

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const totalGrossQty = modelStats?.grossQuantity ?? 0;
  const totalGrossAmt = modelStats?.grossAmount ?? 0;
  const totalDiscountQty = modelStats?.discountQuantity ?? 0;
  const totalDiscountAmt = modelStats?.discountAmount ?? 0;
  const totalNetQty = modelStats?.netQuantity ?? 0;
  const totalNetAmt = modelStats?.netAmount ?? 0;

  if (!modelName) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: HEADER_BG,
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.5)",
            "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Model icon + name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: "14px",
              backgroundColor: "rgba(0,151,215,0.25)",
              border: "1px solid rgba(0,151,215,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {/* model chip icon */}
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="6" width="20" height="12" rx="3" stroke="#0097D7" strokeWidth="1.6"/>
              <circle cx="8" cy="12" r="2" fill="#0097D7"/>
              <circle cx="16" cy="12" r="2" fill="#0097D7"/>
              <path d="M8 6V4M16 6V4M8 18v2M16 18v2" stroke="#0097D7" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
              {modelName}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.55)", mt: 0.3 }}>
              GitHub Copilot AI model · usage breakdown by user
            </Typography>
            {fromDate && toDate && (
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "rgba(255,255,255,0.6)", mt: 0.25 }}>
                {fromDate} → {toDate}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Mini stat row */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <MiniStat label="Users" value={loading ? "…" : totalCount} />
          <MiniStat label="Gross AIC" value={fmt(totalGrossQty)} />
          <MiniStat label="Gross Spend" value={"$" + totalGrossAmt.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
          <MiniStat label="Net Spend" value={"$" + totalNetAmt.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} color={totalNetAmt === 0 ? "rgba(255,255,255,0.4)" : "#fff"} />
        </Box>
      </Box>

      {/* ── Table ── */}
      <DialogContent sx={{ p: 0, backgroundColor: "#f8f8f8", display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {/* Search + Sort bar — only shown when data exists */}
        {users.length > 0 && (
        <Box sx={{ px: 3, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
            User Consumption Breakdown
            {searchQuery && (
              <Box component="span" sx={{ ml: 1, fontWeight: 400, color: "#999", textTransform: "none", letterSpacing: 0 }}>
                · {users.length} matching
              </Box>
            )}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "1.1rem", color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 210,
                "& .MuiOutlinedInput-root": { borderRadius: "6px", backgroundColor: "#f9f9f9" },
              }}
            />
            {/* Sort by Email */}
            <Tooltip title={`Sort by Email${sortBy === "email" ? (sortOrder === "asc" ? " (A→Z)" : " (Z→A)") : ""}`} placement="top">
              <IconButton
                size="small"
                onClick={() => toggleSort("email")}
                sx={{
                  border: sortBy === "email" ? `2px solid ${ACCENT}` : "1px solid #e8e8e8",
                  borderRadius: "6px",
                  backgroundColor: sortBy === "email" ? "#e3f2fd" : "#fff",
                  color: sortBy === "email" ? ACCENT : "#555",
                }}
              >
                {sortBy === "email" && sortOrder === "desc" ? (
                  <ArrowDownwardIcon fontSize="small" />
                ) : sortBy === "email" && sortOrder === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1, px: 0.2 }}>AZ</Typography>
                )}
              </IconButton>
            </Tooltip>
            {/* Sort by AIC */}
            <Tooltip title={`Sort by AIC${sortBy === "gross_quantity" ? (sortOrder === "asc" ? " (Low→High)" : " (High→Low)") : ""}`} placement="top">
              <IconButton
                size="small"
                onClick={() => toggleSort("gross_quantity")}
                sx={{
                  border: sortBy === "gross_quantity" ? `2px solid ${ACCENT}` : "1px solid #e8e8e8",
                  borderRadius: "6px",
                  backgroundColor: sortBy === "gross_quantity" ? "#e3f2fd" : "#fff",
                  color: sortBy === "gross_quantity" ? ACCENT : "#555",
                }}
              >
                {sortBy === "gross_quantity" && sortOrder === "desc" ? (
                  <ArrowDownwardIcon fontSize="small" />
                ) : sortBy === "gross_quantity" && sortOrder === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1, px: 0.2 }}>⚡</Typography>
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        )}

        {loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: ACCENT }} size={36} />
          </Box>
        ) : users.length === 0 ? (
          /* ── Empty state ── */
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 3,
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "14px",
                backgroundColor: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="#bbb" strokeWidth="1.6"/>
                <path d="M12 8v4M12 16h.01" stroke="#bbb" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#555" }}>
              No data available
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#aaa", textAlign: "center", maxWidth: 280 }}>
              No user-level data is available for <strong>{modelName}</strong> in the selected date range.
            </Typography>
          </Box>
        ) : (
          <>
        <TableContainer sx={{ px: 3, pb: 3, maxHeight: 400, overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...hCell, pl: 0 }}>#</TableCell>
                <TableCell sx={hCell}>User</TableCell>
                {isEnterprise && <TableCell sx={hCell}>Org</TableCell>}
                <TableCell sx={hCell} align="right">Gross AIC</TableCell>
                <TableCell sx={hCell} align="right">Gross $</TableCell>
                <TableCell sx={{ ...hCell, color: "#26890d" }} align="right">Discount AIC</TableCell>
                <TableCell sx={{ ...hCell, color: "#26890d" }} align="right">Discount $</TableCell>
                <TableCell sx={hCell} align="right">Net AIC</TableCell>
                <TableCell sx={{ ...hCell, pr: 0 }} align="right">Net $</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user, idx) => {
                const isFullyCovered = user.net_amount === 0 && user.gross_amount > 0;
                const avatarColor = getAvatarColor(user.email ?? user.user_name ?? "");
                const initials = getInitials(user.user_name ?? "");

                return (
                  <TableRow
                    key={user.email}
                    sx={{
                      "&:hover": { backgroundColor: "#eef6ff" },
                      "&:last-child td": { border: 0 },
                    }}
                  >
                    <TableCell sx={{ ...bCell, pl: 0, color: "#bbb", width: 32, verticalAlign: "top", pt: 1.5 }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={{ ...bCell, verticalAlign: "top" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            backgroundColor: avatarColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            color: "#fff",
                            flexShrink: 0,
                            letterSpacing: "0.02em",
                          }}
                        >
                          {initials}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, minWidth: 0 }}>
                            {isFullyCovered && (
                              <Tooltip title="Fully pool-covered — not billed" arrow placement="right">
                                <CheckCircleOutlineIcon sx={{ fontSize: "0.85rem", color: "#26890d", flexShrink: 0 }} />
                              </Tooltip>
                            )}
                            <Tooltip title={user.email} arrow placement="top" enterDelay={400}>
                              <Typography
                                sx={{
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                  color: "#1a1a1a",
                                  lineHeight: 1.3,
                                  maxWidth: 220,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {user.email}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>
                            @{user.user_name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {isEnterprise && (
                      <TableCell sx={bCell}>
                        {user.org_name && (
                          <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>
                            {user.org_name}
                          </Typography>
                        )}
                        {user.cost_center_name && (
                          <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>{user.cost_center_name}</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={bCell} align="right">
                      {fmt(user.gross_quantity)}
                    </TableCell>
                    <TableCell sx={bCell} align="right">
                      {fmtUSD(user.gross_amount)}
                    </TableCell>
                    <TableCell sx={{ ...bCell, color: "#26890d", fontWeight: 600 }} align="right">
                      {user.discount_quantity > 0 ? `-${fmt(user.discount_quantity)}` : "-"}
                    </TableCell>
                    <TableCell sx={{ ...bCell, color: "#26890d", fontWeight: 600 }} align="right">
                      {user.discount_amount > 0 ? `-${fmtUSD(user.discount_amount)}` : "-"}
                    </TableCell>
                    <TableCell sx={{ ...bCell, fontWeight: 600 }} align="right">
                      {fmt(user.net_quantity)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bCell,
                        fontWeight: 700,
                        pr: 0,
                        color: user.net_amount === 0 ? "#bbb" : "#1a1a1a",
                      }}
                      align="right"
                    >
                      {fmtUSD(user.net_amount)}
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* ── Totals row ── */}
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                <TableCell sx={{ ...bCell, pl: 0, border: 0 }} />
                <TableCell sx={{ ...bCell, fontWeight: 700, color: "#555", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", border: 0 }}>
                  Totals
                </TableCell>
                {isEnterprise && <TableCell sx={{ ...bCell, border: 0 }} />}
                <TableCell sx={{ ...bCell, fontWeight: 700, border: 0 }} align="right">{fmt(totalGrossQty)}</TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, border: 0 }} align="right">{fmtUSD(totalGrossAmt)}</TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, color: "#26890d", border: 0 }} align="right">
                  {totalDiscountQty > 0 ? `-${fmt(totalDiscountQty)}` : "-"}
                </TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, color: "#26890d", border: 0 }} align="right">
                  {totalDiscountAmt > 0 ? `-${fmtUSD(totalDiscountAmt)}` : "-"}
                </TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, border: 0 }} align="right">{fmt(totalNetQty)}</TableCell>
                <TableCell sx={{ ...bCell, fontWeight: 700, pr: 0, color: totalNetAmt === 0 ? "#bbb" : "#1a1a1a", border: 0 }} align="right">
                  {fmtUSD(totalNetAmt)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Footer ── */}
        <Divider />
        <Box sx={{ px: 3, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "0.72rem", color: "#bbb" }}>
            {totalCount} user{totalCount !== 1 ? "s" : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: "0.72rem", color: "#555", minWidth: 60, textAlign: "center" }}>
              Page {page} of {totalPages}
            </Typography>
            <IconButton
              size="small"
              disabled={page === totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModelDetailModal;
